import { useDeferredValue, useMemo, useState } from "react";
import { Box, Button, Chip, IconButton, LinearProgress, Paper, Skeleton, Stack, Tooltip, Typography, useMediaQuery } from "@mui/material";
import { Add, Inventory2Outlined, Refresh, StraightenOutlined } from "@mui/icons-material";
import { DataGrid, GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import { useQuery } from "@tanstack/react-query";

import { getShipmentProfilesApi } from "./setupApi";
import { useModal } from "../../../hooks/useModal";
import { EmptyState, ErrorState, PageHeader, SearchField, gridSx, matchesSearch } from "./SetupUI";

export interface ShipmentProfilesType {
  id: number;
  ship_profile_id: string;
  order_channel: string;
  product_name: string;
  description: string;
  length: number;
  width: number;
  height: number;
  weight: number;
  hsn_code: string;
}

export const SHIPMENT_PROFILES_KEY = ["shipment-profiles"];

const fmt = (n: unknown) => {
  const v = Number(n);
  return Number.isFinite(v) ? v.toLocaleString("en-IN", { maximumFractionDigits: 2 }) : "—";
};
const dims = (p: ShipmentProfilesType) => `${fmt(p.length)} × ${fmt(p.width)} × ${fmt(p.height)} cm`;

const ChannelChip = ({ value }: { value?: string }) =>
  value ? (
    <Chip size="small" label={value} sx={{ height: 22, fontSize: 12, fontWeight: 600, bgcolor: "#eef4ff", color: "#1554b3" }} />
  ) : (
    <Typography color="text.disabled">—</Typography>
  );

const columns: GridColDef<ShipmentProfilesType>[] = [
  {
    field: "product_name",
    headerName: "Product",
    minWidth: 220,
    flex: 1.6,
    renderCell: ({ row }: GridRenderCellParams<ShipmentProfilesType>) => (
      <Stack minWidth={0}>
        <Typography fontSize={14} fontWeight={600}>
          {row.product_name || "—"}
        </Typography>
        {row.description && (
          <Typography fontSize={12} color="text.secondary" sx={{ whiteSpace: "normal", wordBreak: "break-word" }}>
            {row.description}
          </Typography>
        )}
      </Stack>
    ),
  },
  { field: "hsn_code", headerName: "HSN code", minWidth: 110, flex: 0.6 },
  {
    field: "dimensions",
    headerName: "Dimensions (L × W × H)",
    minWidth: 170,
    flex: 0.9,
    sortable: false,
    valueGetter: ({ row }) => dims(row),
  },
  {
    field: "weight",
    headerName: "Weight",
    minWidth: 100,
    flex: 0.5,
    type: "number",
    align: "left",
    headerAlign: "left",
    valueFormatter: ({ value }) => `${fmt(value)} kg`,
  },
  {
    field: "order_channel",
    headerName: "Channel",
    minWidth: 130,
    flex: 0.7,
    renderCell: ({ row }: GridRenderCellParams<ShipmentProfilesType>) => <ChannelChip value={row.order_channel} />,
  },
];

const ShipmentProfiles = () => {
  const isMobile = useMediaQuery("(max-width:899px)");
  const { onOpen } = useModal();
  const [search, setSearch] = useState("");
  const deferredSearch = useDeferredValue(search);

  const query = useQuery({
    queryKey: SHIPMENT_PROFILES_KEY,
    queryFn: async () => {
      const res = await getShipmentProfilesApi();
      const list = (res?.data?.data ?? []) as ShipmentProfilesType[];
      return list.map((p, i) => ({ ...p, id: i + 1 }));
    },
    staleTime: 60_000,
    retry: 1,
  });

  const profiles = useMemo(() => query.data ?? [], [query.data]);
  const rows = useMemo(
    () =>
      profiles.filter((p) =>
        matchesSearch([p.product_name, p.description, p.hsn_code, p.order_channel, dims(p), p.weight], deferredSearch)
      ),
    [profiles, deferredSearch]
  );

  const addButton = (
    <Button
      variant="contained"
      disableElevation
      startIcon={<Add />}
      onClick={() => onOpen("profile")}
      sx={{ textTransform: "none", borderRadius: 2, fontWeight: 600 }}
    >
      Add profile
    </Button>
  );

  const empty = (
    <EmptyState
      icon={<Inventory2Outlined />}
      title={deferredSearch ? "No matching profiles" : "No shipment profiles yet"}
      message={
        deferredSearch
          ? `Nothing matches "${deferredSearch.trim()}".`
          : "Save the size and weight of products you ship often, so you don't have to type them for every order."
      }
      action={!deferredSearch && addButton}
    />
  );

  return (
    <>
      <PageHeader
        title="Shipment profiles"
        subtitle="Saved package sizes and weights for the products you ship most."
        action={addButton}
      />

      <Paper variant="outlined" sx={{ borderRadius: 3, overflow: "hidden" }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "stretch", sm: "center" }}
          gap={1.5}
          sx={{ p: 2 }}
        >
          <Typography fontSize={14} color="text.secondary">
            {query.isLoading ? "Loading profiles…" : `${profiles.length} profile${profiles.length === 1 ? "" : "s"}`}
          </Typography>
          <Stack direction="row" gap={1}>
            <SearchField value={search} onChange={setSearch} placeholder="Search product, HSN, channel…" sx={{ flex: 1 }} />
            <Tooltip title="Refresh">
              <span>
                <IconButton
                  onClick={() => query.refetch()}
                  disabled={query.isFetching}
                  sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2 }}
                >
                  <Refresh fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        </Stack>
        {query.isFetching && !query.isLoading ? <LinearProgress sx={{ height: 2 }} /> : <Box sx={{ height: 2 }} />}

        <Box sx={{ p: 2, pt: 1 }}>
          {query.isError ? (
            <ErrorState error={query.error} fallback="Couldn't load shipment profiles." onRetry={() => query.refetch()} />
          ) : isMobile ? (
            query.isLoading ? (
              <Stack gap={1.5}>
                {[0, 1, 2].map((i) => (
                  <Skeleton key={i} variant="rounded" height={96} />
                ))}
              </Stack>
            ) : rows.length === 0 ? (
              empty
            ) : (
              <Stack gap={1.5}>
                {rows.map((p) => (
                  <Paper key={p.id} variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                    <Stack direction="row" justifyContent="space-between" gap={1}>
                      <Box minWidth={0}>
                        <Typography fontWeight={600}>{p.product_name || "—"}</Typography>
                        {p.description && (
                          <Typography fontSize={12} color="text.secondary">
                            {p.description}
                          </Typography>
                        )}
                      </Box>
                      <ChannelChip value={p.order_channel} />
                    </Stack>
                    <Stack direction="row" gap={2} mt={1.5} flexWrap="wrap">
                      <Typography fontSize={13} display="flex" alignItems="center" gap={0.5}>
                        <StraightenOutlined sx={{ fontSize: 16, color: "text.secondary" }} />
                        {dims(p)}
                      </Typography>
                      <Typography fontSize={13}>
                        <b>{fmt(p.weight)} kg</b>
                      </Typography>
                      {p.hsn_code && (
                        <Typography fontSize={13} color="text.secondary">
                          HSN {p.hsn_code}
                        </Typography>
                      )}
                    </Stack>
                  </Paper>
                ))}
              </Stack>
            )
          ) : (
            <Box sx={{ width: "100%", minWidth: 0 }}>
              <DataGrid
                autoHeight
                getRowHeight={() => "auto"}
                rows={rows}
                columns={columns}
                loading={query.isLoading}
                disableRowSelectionOnClick
                pageSizeOptions={[25, 50, 100]}
                initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
                columnHeaderHeight={44}
                slots={{ loadingOverlay: LinearProgress, noRowsOverlay: () => empty }}
                sx={gridSx}
              />
            </Box>
          )}
        </Box>
      </Paper>
    </>
  );
};

export default ShipmentProfiles;
